/*
 * Copyright (c) 2022. American Express Company
 */

// theirs
import { LocalDate } from '@js-joda/core';

// ours
import { TLElement } from './TLElement';
import UnitService from '../../../services/unit.service';
import { JaegerBarHeights } from '../../../services/jaeger.service';
import TLBarArea from './TLBarArea';
import ProjectJaeger from '../ProjectJaeger';
import { BarColumn } from '../data/BarColumn.interface';
import TLBar, { TLBarType } from './TLBar';
import BarFactory from '../factories/bar.factory.singleton';

export default class TLBarColumn extends TLElement {
    public height: JaegerBarHeights;
    public index: number;
    public topBarHeight: number;
    public bottomBarHeight: number;
    public topBarUrl: string;
    public bottomBarUrl: string;
    public barColumn: BarColumn = {};
    public startDate: LocalDate;
    public xPosition: number;
    public parent: TLBarArea;
    public projectJaeger: ProjectJaeger;
    public topBar: TLBar;
    public bottomBar: TLBar;
    private readonly barFactory: BarFactory;

    constructor(barArea: TLBarArea, height: JaegerBarHeights, index: number) {
        super();
        this.barFactory = BarFactory.getInstance();
        this.parent = barArea;
        this.projectJaeger = this.parent.parent;
        this.height = height;
        this.index = index;
    }

    private configureBarColumn(): void {
        this.startDate = this.parent.jaegerCashFlow[this.index].startDate;
        this.xPosition = UnitService.getJaegerX(
            this.projectJaeger.props.context,
            this.projectJaeger.props.user,
            this.startDate
        );

        switch (this.projectJaeger.props.view) {
            case 'networth':
                this.topBarHeight = this.height.netWorth;
                this.topBarUrl =
                    this.projectJaeger.state.zoom === 'months'
                        ? `/show-month/networth/${this.index}`
                        : `/show/networth/${this.index}`;
                break;
            case 'investment-debt':
                this.topBarHeight = this.height.investment;
                this.bottomBarHeight = this.height.debt;
                this.topBarUrl =
                    this.projectJaeger.state.zoom === 'months'
                        ? `/show-month/assets/${this.index}`
                        : `/show/assets/${this.index}`;
                this.bottomBarUrl =
                    this.projectJaeger.state.zoom === 'months'
                        ? `/show-month/debts/${this.index}`
                        : `/show/debts/${this.index}`;
                break;
            case 'income-expense':
            default:
                this.topBarHeight = this.height.income;
                this.bottomBarHeight = this.height.expense;
                this.topBarUrl =
                    this.projectJaeger.state.zoom === 'months'
                        ? `/show-month/incomes/${this.index}`
                        : `/show/incomes/${this.index}`;
                this.bottomBarUrl =
                    this.projectJaeger.state.zoom === 'months'
                        ? `/show-month/expenses/${this.index}`
                        : `/show/expenses/${this.index}`;
        }
    }

    public upsert(height?: JaegerBarHeights, index?: number): void {
        if (height != null) this.height = height;
        if (index != null) this.index = index;
        this.configureBarColumn();
        const newTopBar = !this.topBar;
        const newBottomBar = !this.bottomBar;
        if (newTopBar) this.topBar = new TLBar(this, TLBarType.TOP);
        if (newBottomBar) this.bottomBar = new TLBar(this, TLBarType.BOTTOM);
        this.barFactory.build(this.topBar);
        this.barFactory.build(this.bottomBar);
        this.barColumn.topBar = this.topBar.barGroup;
        this.barColumn.bottomBar = this.bottomBar.barGroup;
    }

    public create(): void {
        this.upsert();
    }

    public destroy(): void {
        this.topBar.destroy();
        this.bottomBar.destroy();
        this.topBar = null;
        this.bottomBar = null;
    }

    public remove(): void {}

    public updateX(): void {
        if (this.topBar) this.topBar.updateX();
        if (this.bottomBar) this.bottomBar.updateX();
    }

    public updateY(): void {}
}
